"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DateRangePickerProps {
  /** The selected date range */
  value?: DateRange
  /** Callback when the date range changes */
  onChange?: (date: DateRange | undefined) => void
  /** Placeholder text when no date is selected */
  placeholder?: string
  /** Number of months to display */
  numberOfMonths?: number
  /** Whether the date picker is disabled */
  disabled?: boolean
  /** Alignment of the popover */
  align?: "center" | "start" | "end"
  /** Additional CSS classes */
  className?: string
  /** Whether to show the year switcher */
  showYearSwitcher?: boolean
  /** The number of years to display in year view */
  yearRange?: number
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = "Pick a date range",
  numberOfMonths = 2,
  disabled = false,
  align = "start",
  className,
  showYearSwitcher = true,
  yearRange = 10,
}: DateRangePickerProps) {
  const [date, setDate] = React.useState<DateRange | undefined>(value)

  React.useEffect(() => {
    setDate(value)
  }, [value])

  const handleDateChange = (newDate: DateRange | undefined) => {
    setDate(newDate)
    onChange?.(newDate)
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0" 
          align={align}
        >
          <Calendar
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleDateChange}
            numberOfMonths={numberOfMonths}
            disabled={disabled}
            showYearSwitcher={showYearSwitcher}
            yearRange={yearRange}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}